import React, { useState } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import { Shield, Key, RefreshCw, Copy, Check } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AdminProfilePage = () => {
    const [loading, setLoading] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);

    const generatePassword = () => {
        const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
        let password = "";
        for (let i = 0; i < 16; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setPasswordData({
            ...passwordData,
            newPassword: password,
            confirmPassword: password
        });
        return password;
    };

    const copyToClipboard = () => {
        if (!passwordData.newPassword) return;
        navigator.clipboard.writeText(passwordData.newPassword);
        setCopied(true);
        toast.success('Password copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            return toast.error('Passwords do not match');
        }
        if (passwordData.newPassword.length < 6) {
            return toast.error('Password must be at least 6 characters long');
        }

        setLoading(true);
        try {
            await api.put('/admin/profile/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            toast.success('Password changed successfully');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Shield className="text-blue-400" /> Admin Profile
                        </h1>
                        <p className="text-gray-400 text-sm">Manage your account settings</p>
                    </div>
                </div>
            </div>

            <div className="p-6 max-w-4xl mx-auto">
                <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 rounded-lg bg-blue-500/20 text-blue-400">
                            <Key size={24} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Change Password</h2>
                            <p className="text-gray-400 text-sm">Update your admin account password</p>
                        </div>
                    </div>

                    <form onSubmit={handlePasswordChange} className="space-y-6 max-w-lg">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Current Password</label>
                            <input
                                required
                                type="password"
                                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                value={passwordData.currentPassword}
                                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                            />
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-400 mb-2">New Password</label>
                            <div className="flex gap-2">
                                <input
                                    required
                                    type={showPassword ? "text" : "password"}
                                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={generatePassword}
                                    className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-gray-300 hover:text-white hover:bg-slate-600 tooltip"
                                    title="Generate Strong Password"
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </div>
                            {passwordData.newPassword && (
                                <div className="mt-2 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="text-xs text-blue-400 hover:text-blue-300 underline"
                                    >
                                        {showPassword ? "Hide Password" : "Show Password"}
                                    </button>
                                    <span className="text-gray-600">|</span>
                                    <button
                                        type="button"
                                        onClick={copyToClipboard}
                                        className="text-xs flex items-center gap-1 text-blue-400 hover:text-blue-300"
                                    >
                                        {copied ? <Check size={12} /> : <Copy size={12} />}
                                        {copied ? "Copied" : "Copy Password"}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">Confirm New Password</label>
                            <input
                                required
                                type="password"
                                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                value={passwordData.confirmPassword}
                                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                            />
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-blue-500 transition disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {loading ? 'Updating Password...' : 'Update Password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdminProfilePage;
